import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConfirm } from '../../components/ui/ConfirmProvider';

const DEFAULT_MESSAGE =
  'You have unsaved changes. If you leave now, those changes may be lost.';

/**
 * Warns before leaving a page with unsaved work:
 * - native beforeunload (refresh / close tab)
 * - in-app <a>/<Link> clicks away from the current URL
 * - callers can also `await confirmLeave()` before programmatic navigate
 */
export function useUnsavedChangesGuard(
  isDirty: boolean,
  options?: { message?: string; title?: string },
) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const title = options?.title ?? 'Unsaved changes';
  const message = options?.message ?? DEFAULT_MESSAGE;

  const confirmLeave = useCallback(async (): Promise<boolean> => {
    if (!isDirty) return true;
    return confirm({
      title,
      message,
      confirmText: 'Leave',
      cancelText: 'Stay',
      confirmVariant: 'danger',
    });
  }, [isDirty, confirm, title, message]);

  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as HTMLElement | null)?.closest?.(
        'a[href]',
      ) as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('javascript:')
      ) {
        return;
      }

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      const next = `${url.pathname}${url.search}${url.hash}`;
      const current = `${location.pathname}${location.search}${location.hash}`;
      if (next === current) return;

      event.preventDefault();
      event.stopPropagation();

      void (async () => {
        if (await confirmLeave()) {
          navigate(next);
        }
      })();
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [
    isDirty,
    confirmLeave,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  return { confirmLeave };
}
